import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Unlock } from 'lucide-react';
import { EDIT_LOCK_LABELS, type EditLockResource } from '@erp/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/table';
import { Field, Input } from '@/components/ui/input';
import { EmptyState, ErrorState } from '@/components/ui/misc';
import { useConfirm } from '@/components/ui/use-confirm';
import { toast } from 'sonner';

/**
 * Bloqueos de edición vigentes, para el administrador.
 *
 * Un bloqueo caduca solo a los 15 minutos sin actividad, pero cuando alguien se va y deja una ficha
 * abierta no siempre se puede esperar: aquí se ve quién la tiene y se libera con un motivo, que
 * queda en la auditoría.
 */

interface Bloqueo {
  resourceType: EditLockResource;
  resourceId: string;
  fullName: string;
  username: string;
  terminalCode: string | null;
  lockedAt: string;
  heartbeatAt: string;
  expiresAt: string;
}

const hora = (iso: string) => new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

export function EditLocksPanel() {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [motivo, setMotivo] = useState('');

  const bloqueos = useQuery({
    queryKey: ['edit-locks', 'active'],
    queryFn: () => api<{ data: Bloqueo[] }>('/edit-locks/active'),
    // Se refresca solo: los bloqueos aparecen y caducan sin que nadie toque esta pantalla.
    refetchInterval: 30_000
  });

  const filas = useMemo(() => bloqueos.data?.data ?? [], [bloqueos.data]);

  const liberar = async (bloqueo: Bloqueo) => {
    // Se valida **antes** de abrir el diálogo: si no, el usuario confirma y luego se encuentra con
    // el error, sin saber que le faltaba el motivo.
    if (motivo.trim().length < 3) {
      toast.error('Escribe el motivo de la liberación antes de continuar');
      return;
    }
    const sigue = await confirm({
      title: `¿Liberar «${EDIT_LOCK_LABELS[bloqueo.resourceType] ?? bloqueo.resourceType}»?`,
      description: `${bloqueo.fullName} dejará de poder guardar en cuanto lo intente. El motivo queda en la auditoría.`,
      confirmLabel: 'Liberar bloqueo',
      destructive: true
    });
    if (!sigue) return;
    try {
      await api('/edit-locks/force-release', {
        method: 'POST',
        body: {
          resourceType: bloqueo.resourceType,
          resourceId: bloqueo.resourceId,
          reason: motivo.trim()
        }
      });
      toast.success('Bloqueo liberado');
      setMotivo('');
      await qc.invalidateQueries({ queryKey: ['edit-locks'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo liberar el bloqueo');
    }
  };

  const columnas: Column<Bloqueo>[] = [
    { key: 'resourceType', header: 'Registro', cell: (fila) => (
      <div>
        <p className="font-medium">{EDIT_LOCK_LABELS[fila.resourceType] ?? fila.resourceType}</p>
        <p className="font-mono text-[10px] text-muted-foreground">{fila.resourceId.slice(0, 8)}</p>
      </div>
    ) },
    { key: 'quien', header: 'Lo tiene', cell: (fila) => (
      <div>
        <p>{fila.fullName}</p>
        <p className="text-[11px] text-muted-foreground">{fila.username}{fila.terminalCode ? ` · ${fila.terminalCode}` : ''}</p>
      </div>
    ) },
    { key: 'desde', header: 'Desde', cell: (fila) => hora(fila.lockedAt) },
    { key: 'actividad', header: 'Última actividad', cell: (fila) => hora(fila.heartbeatAt) },
    { key: 'caduca', header: 'Caduca', cell: (fila) => hora(fila.expiresAt) },
    { key: 'acciones', header: '', cell: (fila) => (
      <Button variant="outline" size="sm" onClick={() => void liberar(fila)}>
        <Unlock className="mr-1.5 size-3.5" /> Liberar
      </Button>
    ) }
  ];

  return (
    <div className="space-y-3">
      {dialog}
      <Card className="space-y-2 p-3">
        <p className="text-xs text-muted-foreground">
          Estos son los registros que alguien tiene abiertos para editar ahora mismo. Caducan solos a los
          15 minutos sin actividad; libera aquí solo si hace falta que otro pueda entrar ya.
        </p>
        <Field label="Motivo de la liberación" hint="Se guarda en la auditoría junto con el bloqueo">
          <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Se ha ido y ha dejado la ficha abierta" />
        </Field>
      </Card>
      {bloqueos.isError ? (
        <ErrorState message="No se pudieron cargar los bloqueos" onRetry={() => void bloqueos.refetch()} />
      ) : filas.length === 0 && !bloqueos.isLoading ? (
        <EmptyState title="Nadie está editando nada" description="No hay bloqueos vigentes en este momento." icon={Lock} />
      ) : (
        <Card className="p-0">
          <DataTable columns={columnas} rows={filas} loading={bloqueos.isLoading} rowKey={(fila) => `${fila.resourceType}:${fila.resourceId}`} />
        </Card>
      )}
    </div>
  );
}
